import { Injectable } from "@angular/core";
import { CotecnaUserService } from "@cotecna/session";
import {
  ACCOUNT_NAME_ALEXANDRIA,
  ACCOUNT_NAME_BRAZIL,
  ACCOUNT_NAME_COTONOU,
  ACCOUNT_NAME_DELHI,
  ACCOUNT_NAME_DUBAI,
  ACCOUNT_NAME_DURBAN,
  ACCOUNT_NAME_ISTANBUL,
  ACCOUNT_NAME_MUMBAI,
  ACCOUNT_NAME_NAIROBI,
  ACCOUNT_NAME_NANTES,
  ACCOUNT_NAME_NETHERLANDS,
  ACCOUNT_NAME_SINGAPORE,
  ADMIN_ROLE,
  COORDINATOR_READ_ONLY_ROLE,
  COORDINATOR_ROLE,
  CUSTOMER_ROLE,
  GLOBAL_MANAGER_ROLE,
  INSPECTOR_ROLE,
  MANAGER_ROLE,
  SERVICE_PROVIDER_COORDINATOR_ROLE,
  SERVICE_PROVIDER_INSPECTOR_ROLE,
  SYSTEM_ADMIN_ROLE,
} from "src/app/inspection/constants/constants";

export enum AccountType {
  InspectionsOnly,
  TimeManagementOnly,
  InspectionsAndTimeManagement,
}

@Injectable({
  providedIn: "root",
})
export class UserRolePermissionsService {
  private currentUserRoles: any;
  private readonly currentAccountType: AccountType;

  private constructor(
    private readonly connectedUserService: CotecnaUserService,
  ) {
    const user = connectedUserService.getUser() as any;
    this.currentUserRoles = user.roles;
    this.currentAccountType = user.accountType;
  }

  public canCreate(): boolean {
    return this.currentUserRoles?.includes(COORDINATOR_ROLE) ?? false;
  }

  public canAccessToInspectionsList(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(COORDINATOR_READ_ONLY_ROLE) ||
        this.currentUserRoles.includes(COORDINATOR_ROLE) ||
        this.currentUserRoles.includes(INSPECTOR_ROLE) ||
        this.currentUserRoles.includes(SERVICE_PROVIDER_COORDINATOR_ROLE) ||
        this.currentUserRoles.includes(SERVICE_PROVIDER_INSPECTOR_ROLE))
    );
  }

  public canAccessToTimesheetActivities(): boolean {
    return (
      this.currentUserRoles?.includes(COORDINATOR_ROLE)
    );
  }

  public canAccessToTimesheetManagement(): boolean {
    return (
      this.currentUserRoles?.includes(COORDINATOR_ROLE) ||
      this.currentUserRoles.includes(MANAGER_ROLE) ||
      this.currentUserRoles.includes(GLOBAL_MANAGER_ROLE)
    );
  }

  public canAccessToResources(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(MANAGER_ROLE) ||
        this.currentUserRoles.includes(GLOBAL_MANAGER_ROLE) ||
        this.currentUserRoles.includes(COORDINATOR_ROLE) ||
        this.currentUserRoles.includes(SYSTEM_ADMIN_ROLE) ||
        this.currentUserRoles.includes(ADMIN_ROLE))
    );
  }

  public canAccessToSettings(): boolean {
    return this.isAdminOrSystemAdmin();
  }

  public canAccessToDashboards(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(MANAGER_ROLE) ||
        this.currentUserRoles.includes(GLOBAL_MANAGER_ROLE))
    );
  }

  public canAccessToDashboard(accountName: string): boolean {
    if (accountName === ACCOUNT_NAME_NETHERLANDS) {
      return (
        this.currentUserRoles &&
        (this.currentUserRoles.includes(MANAGER_ROLE) ||
          this.currentUserRoles.includes(COORDINATOR_ROLE) || 
          this.currentUserRoles.includes(GLOBAL_MANAGER_ROLE))
      );
    }

    if (accountName === ACCOUNT_NAME_BRAZIL) {
      return (
        this.currentUserRoles &&
        (this.currentUserRoles.includes(MANAGER_ROLE) ||
          this.currentUserRoles.includes(COORDINATOR_ROLE) || 
          this.currentUserRoles.includes(GLOBAL_MANAGER_ROLE))
      );
    }

    const vocAccounts = [
      ACCOUNT_NAME_DURBAN,
      ACCOUNT_NAME_DUBAI,
      ACCOUNT_NAME_NANTES,
      ACCOUNT_NAME_MUMBAI,
      ACCOUNT_NAME_COTONOU,
      ACCOUNT_NAME_ALEXANDRIA,
      ACCOUNT_NAME_ISTANBUL,
      ACCOUNT_NAME_DELHI,
      ACCOUNT_NAME_NAIROBI,
      ACCOUNT_NAME_SINGAPORE,
    ];

    if (vocAccounts.includes(accountName)) {
      if (accountName === ACCOUNT_NAME_DELHI) {
        return (
          this.currentUserRoles &&
          (this.currentUserRoles.includes(MANAGER_ROLE) ||
            this.currentUserRoles.includes(COORDINATOR_ROLE) ||
            this.currentUserRoles.includes(GLOBAL_MANAGER_ROLE) ||
            this.currentUserRoles.includes(CUSTOMER_ROLE))
        );
      }
      return (
        this.currentUserRoles &&
        (this.currentUserRoles.includes(MANAGER_ROLE) ||
          this.currentUserRoles.includes(COORDINATOR_ROLE) ||
          this.currentUserRoles.includes(GLOBAL_MANAGER_ROLE))
      );
    }

    return (this.currentUserRoles && (this.currentUserRoles.includes(MANAGER_ROLE) ||
        this.currentUserRoles.includes(COORDINATOR_ROLE) || this.currentUserRoles.includes(GLOBAL_MANAGER_ROLE))
    );
  }

  public canAccessToAbout(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(COORDINATOR_ROLE) ||
        this.currentUserRoles.includes(COORDINATOR_READ_ONLY_ROLE) ||
        this.currentUserRoles.includes(INSPECTOR_ROLE) ||
        this.currentUserRoles.includes(ADMIN_ROLE) ||
        this.currentUserRoles.includes(SYSTEM_ADMIN_ROLE) ||
        this.currentUserRoles.includes(MANAGER_ROLE) ||
        this.currentUserRoles.includes(GLOBAL_MANAGER_ROLE) ||
        this.currentUserRoles.includes(CUSTOMER_ROLE))
    );
  }

  public canAccessToHistoryLog(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(COORDINATOR_ROLE) ||
        this.currentUserRoles.includes(INSPECTOR_ROLE))
    );
  }

  public isInspector(): boolean {
    return (
      this.currentUserRoles &&
      !this.currentUserRoles.includes(COORDINATOR_ROLE) &&
      this.currentUserRoles.includes(INSPECTOR_ROLE)
    );
  }

  public hasInspectorRole(): boolean {
    return (
      (this.currentUserRoles.includes(INSPECTOR_ROLE) ||
        this.currentUserRoles.includes(SERVICE_PROVIDER_INSPECTOR_ROLE))
    );
  }

  public isReadOnly(): boolean {
    return (
      this.currentUserRoles &&
      !this.currentUserRoles.includes(COORDINATOR_ROLE) &&
      this.currentUserRoles.includes(COORDINATOR_READ_ONLY_ROLE)
    );
  }

  public isCustomer(): boolean {
    return this.currentUserRoles?.includes(CUSTOMER_ROLE) ?? false;
  }

  public canEditReleases(): boolean {
    return this.isAdminOrSystemAdmin();
  }

  public canDownloadOrDeletePictures(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(ADMIN_ROLE) ||
        this.currentUserRoles.includes(SYSTEM_ADMIN_ROLE) ||
        this.currentUserRoles.includes(COORDINATOR_READ_ONLY_ROLE) ||
        this.currentUserRoles.includes(COORDINATOR_ROLE))
    );
  }

  public canUploadPictures(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(INSPECTOR_ROLE) ||
        this.currentUserRoles.includes(SERVICE_PROVIDER_INSPECTOR_ROLE))
    );
  }

  public isServiceProviderCoordinator(): boolean {
    return (
      this.currentUserRoles &&
      !this.currentUserRoles.includes(COORDINATOR_ROLE) &&
      this.currentUserRoles.includes(SERVICE_PROVIDER_COORDINATOR_ROLE)
    );
  }

  public isCoordinatorOrServiceProviderCoordinator(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(COORDINATOR_ROLE) ||
      this.currentUserRoles.includes(SERVICE_PROVIDER_COORDINATOR_ROLE))
    );
  }

  public isServiceProviderInspector(): boolean {
    return (
      this.currentUserRoles &&
      !this.currentUserRoles.includes(COORDINATOR_ROLE) &&
      this.currentUserRoles.includes(SERVICE_PROVIDER_INSPECTOR_ROLE)
    );
  }

  public isServiceProvider(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(SERVICE_PROVIDER_COORDINATOR_ROLE) ||
        this.currentUserRoles.includes(SERVICE_PROVIDER_INSPECTOR_ROLE))
    );
  }

  public isDocumentShareAllowed(accountName: string): boolean {
    if (accountName === ACCOUNT_NAME_NETHERLANDS) {
      return (
        this.currentUserRoles &&
        (this.currentUserRoles.includes(INSPECTOR_ROLE) ||
          this.currentUserRoles.includes(COORDINATOR_ROLE) ||
          this.currentUserRoles.includes(SERVICE_PROVIDER_INSPECTOR_ROLE) ||
          this.currentUserRoles.includes(SERVICE_PROVIDER_COORDINATOR_ROLE))
      );
    } else {
      return (
        this.currentUserRoles &&
        (this.currentUserRoles.includes(COORDINATOR_ROLE) ||
          this.currentUserRoles.includes(SERVICE_PROVIDER_COORDINATOR_ROLE))
      );
    }
  }

  public canAccessToInspectorCapacityTab(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(COORDINATOR_ROLE) ||
        this.currentUserRoles.includes(ADMIN_ROLE) ||
        this.currentUserRoles.includes(SYSTEM_ADMIN_ROLE))
    );
  }

  public isTimeManagementOnlyAccount(): boolean {
    return this.currentAccountType === AccountType.TimeManagementOnly;
  }

  public isInspectionsOnlyAccount(): boolean {
    return this.currentAccountType === AccountType.InspectionsOnly;
  }

  public isInspectionsAndTimeManagementAccount(): boolean {
    return this.currentAccountType === AccountType.InspectionsAndTimeManagement;
  }

  private isAdminOrSystemAdmin(): boolean {
    return (
      this.currentUserRoles &&
      (this.currentUserRoles.includes(ADMIN_ROLE) ||
        this.currentUserRoles.includes(SYSTEM_ADMIN_ROLE))
    );
  }
}
